import { useAlerts } from "./useAlerts";
import AlertsTable from './AlertsTable/AlertsTable';
import { useMemo } from "react";
import { CustomizedDataGrid } from "components/customized_data_grid";

interface AlertsTableWrapperProps {
  idleFilterIcon?: string;
}

export interface EWSColumnConfig {
  key: string;
  name: string; // MUST be 'name', not 'header'
  sortable?: boolean;
  resizable?: boolean;
  renderCell?: (props: { row: any }) => React.ReactNode;
}

export function applyDynamicWidths(
  columns: any[],
  data: any[],
  padding = 50, // Extra padding in pixels for a comfortable fit
  minColWidth = 140, // Absolute minimum column width
  maxColWidth = 500  // Cap max width so long SQL queries don't stretch forever
): any[] {
  if (!Array.isArray(columns)) return [];

  return columns.map((col) => {
    const key = col.key;
    const headerText = col.name || col.header || '';

    // 1. Start with the character length of the header
    let maxCharLength = headerText.toString().length;

    // 2. Check the character length of data in each row for this column
    if (Array.isArray(data)) {
      data.forEach((row) => {
        const val = row[key];
        if (val !== undefined && val !== null) {
          const strVal = typeof val === 'object' ? JSON.stringify(val) : val.toString();
          if (strVal.length > maxCharLength) {
            maxCharLength = strVal.length;
          }
        }
      });
    }

    // 3. Approximate pixel width (~8-10px per character) + comfortable extra padding
    const calculatedWidth = Math.min(
      maxColWidth,
      Math.max(minColWidth, maxCharLength * 9 + padding)
    );

    return {
      ...col,
      width: calculatedWidth,     // Sets the initial width dynamically
      minWidth: calculatedWidth,  // Prevents shrinking below calculated size
      resizable: true,             // Allows manual drag resizing
    };
  });
}

export function getDynamicColumns(): EWSColumnConfig[] {
  return [
    { key: 'ruleName', name: 'Rule Name', sortable: true },
    { key: 'domain', name: 'Domain', sortable: true },
    { key: 'component', name: 'Component', sortable: true },
    { key: 'ewsSignalName', name: 'EWS Signal Name', sortable: true },
    { key: 'riskLevel', name: 'Risk Level', sortable: true },
    // --- Configuration & Status ---
    { key: 'dataSource', name: 'Data Source', sortable: true },
    { key: 'layerTool', name: 'Layer / Tool', sortable: true },
    { key: 'handler', name: 'Handler', sortable: true },
    { key: 'handlerType', name: 'Handler Type', sortable: true },
    { key: 'handlerStatus', name: 'Handler Status', sortable: true },
    // --- Performance Metrics & Query Details ---
    { 
      key: 'durationMs', 
      name: 'Duration (s)', 
      sortable: true,
      renderCell: ({ row }) => `${((row.durationMs || 0) / 1000).toFixed(2)}s`
    },
    { 
      key: 'avgLastTenDuration', 
      name: 'Avg Last 10 Duration (s)', 
      sortable: true,
      renderCell: ({ row }) => 
        typeof row.avgLastTenDuration === 'number' 
          ? `${(row.avgLastTenDuration / 1000).toFixed(2)}s` 
          : row.avgLastTenDuration
    },
    { key: 'resourceName', name: 'Resource Name', sortable: true },
    { key: 'resourceId', name: 'Resource ID', sortable: true },
    { key: 'ruleId', name: 'Rule ID', sortable: true },

    // --- Timestamps ---
    { key: 'createdAt', name: 'Created Date', sortable: true }
  ];
}

export function flattenAlertItem(item: any) {
  return {
    id: item?._id || item?.id || item?.resultId,
    ruleId: item?.ruleId,
    ruleName: item?.ruleConfig?.Rule_Name ?? 'N/A',
    domain: item?.ruleConfig?.Domain ?? 'N/A',
    component: item?.ruleConfig?.Component ?? 'N/A',
    ewsSignalName: item?.ruleConfig?.EWS_Signal_Name ?? 'N/A',
    riskLevel: item?.ruleConfig?.Risk_Level ?? 'N/A',
    dataSource: item?.ruleConfig?.Data_Source ?? 'N/A',
    layerTool: item?.ruleConfig?.Layer_Tool ?? 'N/A',
    handler: item?.ruleConfig?.Handler ?? 'N/A',
    handlerType: item?.ruleConfig?.Handler_Type ?? 'N/A',
    handlerStatus: item?.ruleConfig?.Handler_status ?? 'N/A',
    durationMs: item?.pepwise?.body?.execution_details?.duration_ms ?? 'N/A',
    avgLastTenDuration: item?.pepwise?.body?.avg_last10_duration ?? 'N/A',
    resourceName: item?.pepwise?.body?.resource_name ?? 'N/A',
    resourceId: item?.pepwise?.body?.resource_id ?? 'N/A',
    createdAt: item?.createdAt ?? 'N/A',
    // Retain raw object reference if needed
    raw: item
  };
}

const AlertsTableWrapper = ({ idleFilterIcon }: AlertsTableWrapperProps) => {
    const {
    groupedData,
    totalAlerts,
    page,
    pageSize,
    setPage,
    setPageSize,
    filterOptions,
    sortingInfo,
    setSortingInfo,
    isLoading,
    selectedTile,
    setGroupBy,
    groupByList,
    groupBy,
    includeGroupByMenuColumns,
    handleGroupToggle,
    handleShowMoreClick,
    loadingGroupId,
    isDataOpsAdmin,
    handleManageSop,
    columnFilters,
    handleColumnFilterChange,
    getColumnFilterValues,
  } = useAlerts();

   const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

    // 1. Check if EWSCritical tile is currently selected
  const isEWSCritical = selectedTile?.key === 'EWSCritical';

    // 1. Safely extract raw array from groupedData avoiding TS2339 'never' error
  const rawArray = useMemo(() => {
    if (!groupedData) return [];
    if (Array.isArray(groupedData)) return groupedData;
    
    // Cast groupedData as any to safely check .data or .records without TypeScript complaints
    const dataObj = groupedData as any;
    if (Array.isArray(dataObj?.data)) return dataObj.data;
    if (Array.isArray(dataObj?.records)) return dataObj.records;
    
    return [];
  }, [groupedData]);

  // 2. Flatten raw Mongo query log documents for the data grid
  const tableDataWithDuplicates = useMemo(() => {
    if (!isEWSCritical) return [];
    return rawArray.map(flattenAlertItem);
  }, [rawArray, isEWSCritical]);

  // 3. Get standard column definitions
  const baseEWSColumns = useMemo(() => {
    if (!isEWSCritical) return [];
    return getDynamicColumns();
  }, [isEWSCritical, getDynamicColumns]);

  // 4. Dynamically calculate column widths based on row content
  const EWScolumns = useMemo(() => {
    if (!isEWSCritical) return [];
    return applyDynamicWidths(baseEWSColumns, tableDataWithDuplicates, 60);
  }, [baseEWSColumns, tableDataWithDuplicates, isEWSCritical, applyDynamicWidths]);

  // -------------------------------------------------------------
  // BRANCH 1: EWS Critical Tile -> Render CustomizedDataGrid directly
  // -------------------------------------------------------------
  if (isEWSCritical) {
    return(
   <CustomizedDataGrid
        key="snow-unified-alerts-grid"
        tableColumns={EWScolumns}
        tableData={tableDataWithDuplicates}
        tableType="EWS Mongo Alert"
        rowHeight={45}
        defaultColumnNames={baseEWSColumns}
        listLength={totalAlerts}
        page={page}
        pageSize={pageSize}
        paginationData={{
          totalElements: totalAlerts,
          totalPages: Math.ceil(totalAlerts / pageSize) || 1
        }}
        onSorting={setSortingInfo}
        currSorting={sortingInfo}
        handlePageChange={(_: any, p: number) => setPage(p)}
        handlepageSizeChange={(e: any) => handlePageSizeChange(Number.parseInt(e.target.value, 10))}
        isFetching={isLoading}
        isDateRangeFilter={false}
        isDisabled={false}
        isShowMoreButton={false}
        pageSizeOptions={[10, 50, 100, 200, 500, 1000]}
        isPrevNextPagination
        isRenderPagination={totalAlerts > 0}
        isGroupBy={false}
        isSortIcon={!idleFilterIcon}
        idleFilterIcon={idleFilterIcon}
        style={{ minHeight: "130px" }}
      />
    );
  }

  
  return (
    <AlertsTable
      alerts={groupedData}
      total={totalAlerts}
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      filterOptions={filterOptions}
      onPageSizeChange={handlePageSizeChange}
      onSorting={setSortingInfo}
      sortingInfo={sortingInfo}
      isLoading={isLoading}
      title={selectedTile?.label}
      onGroupByChange={setGroupBy}
      groupByList={groupByList}
      groupBy={groupBy}
      includeGroupByMenuColumns={includeGroupByMenuColumns}
      onGroupToggle={handleGroupToggle}
      onShowMoreClick={handleShowMoreClick}
      loadingGroupId={loadingGroupId}
      isDataOpsAdmin={isDataOpsAdmin}
      onManageSop={handleManageSop}
      idleFilterIcon={idleFilterIcon}
      columnFilters={columnFilters}
      onColumnFilterChange={handleColumnFilterChange}
      getColumnFilterValues={getColumnFilterValues}
    />
  );
};

export default AlertsTableWrapper;